/* DrinksNation "Tell me when it is back" (S5-12, 16 Sep 2026). Loaded by snippets/dn-restock.liquid
   and nothing else, and that snippet renders only on a product page for a variant Shopify reports as
   unavailable - so on most days this file is never fetched at all.

   It posts one thing: {variant_id, product_id} to the app proxy at /apps/dn/api/stock-request. The
   Brain records that a signed-in customer asked, and the `back_in_stock` rule turns a restocked
   request into one message. Nothing here decides what is in stock, and nothing here decides whether
   a message is sent: Shopify owns inventory, the Brain owns the rule.

   What it must do, in the order it matters:
   1. Never break the product page. A 401 (the proxy is not configured), a 503, a 404, HTML instead
      of JSON, a network error: the control takes itself off the page and NOTHING is written to the
      console. The rest of the page - price, Add to cart, the lot - is untouched. That is the v1.1
      rule "the storefront works with the Brain down".
   2. Never queue two requests for one shopper. The endpoint is idempotent while a request is
      outstanding and answers `already: true`, so a second press is safe and is answered honestly
      rather than being swallowed by a disabled button.
   3. Never move the page. The message line has its height reserved in dn-restock.css and the
      button's label never changes, so an answer coming back shifts nothing.

   The button stays enabled throughout (except while a press is in flight). It keeps the focused
   element in the tab order, which a disabled button would not, and repeat presses are exactly what
   the idempotent endpoint is for.

   The pure half is exported for node: qa/unit/restock-control.test.mjs. */
(function (root, factory) {
  var api = factory(root);
  if (typeof module === 'object' && module.exports) module.exports = api;
  root.dnRestock = api;
  if (typeof document !== 'undefined' && typeof fetch === 'function') api.boot();
})(typeof window !== 'undefined' ? window : globalThis, function () {
  'use strict';

  /* Every sentence the control can say. Plain, specific, Singapore English: no date is promised,
     because nobody here knows one - Shopify owns inventory and the restock is set by whatever
     watches it. */
  var COPY = {
    noted: 'Noted. We will email you when it is back in stock.',
    already: 'Already noted. We will email you when it is back in stock.',
    retry: 'That did not save. Try again.',
    signin: 'Sign in and we will email you when it is back.'
  };

  /* What the reply means for the control.
       'noted'   the request was written just now
       'already' this customer had already asked, and nothing was written a second time
       'signin'  the session is not signed in any more (403 login_required)
       'retry'   we sent something the Brain would not take (400); worth one more press
       'gone'    the Brain is down or not configured, and the control cannot promise anything
     Anything not listed is 'gone' on purpose: a control that cannot keep its promise should not sit
     on the page offering to. */
  function outcome(status, body) {
    if (status === 200 && body && body.ok) return body.already ? 'already' : 'noted';
    if (status === 403) return 'signin';
    if (status === 400) return 'retry';
    return 'gone';
  }

  /** The body posted for a variant. `product_id` is optional and is left out when it is not known. */
  function payload(variantId, productId) {
    var body = { variant_id: String(variantId || '') };
    if (productId) body.product_id = String(productId);
    return body;
  }

  function boot() {
    var roots = document.querySelectorAll('[data-dn-restock]');
    for (var i = 0; i < roots.length; i++) bind(roots[i]);
  }

  function bind(el) {
    var button = el.querySelector('[data-dn-restock-ask]');
    if (!button || el.hasAttribute('data-dn-bound')) return;
    el.setAttribute('data-dn-bound', '');

    var url = el.getAttribute('data-url');
    var variantId = el.getAttribute('data-variant-id');
    var productId = el.getAttribute('data-product-id');
    var msg = el.querySelector('[data-dn-restock-msg]');
    var busy = false;

    function say(text) {
      if (msg) msg.textContent = text;
    }

    /* The session lapsed between the page loading and the press. Offer the way back rather than an
       error: the link is built from the theme's own account route, never from anything the Brain
       sent. */
    function offerSignIn() {
      if (!msg) return;
      var href = el.getAttribute('data-login-url');
      msg.textContent = '';
      if (href) {
        var a = document.createElement('a');
        a.setAttribute('href', href);
        a.textContent = 'Sign in';
        msg.appendChild(a);
        msg.appendChild(document.createTextNode(' and we will email you when it is back.'));
      } else {
        msg.textContent = COPY.signin;
      }
      button.hidden = true;
    }

    function settle(state) {
      /* Gone means the Brain cannot be reached or is not configured. The control leaves the page
         rather than sitting there offering a promise it cannot keep, and says nothing about it: the
         shopper asked about a carton, not about our infrastructure. Focus falls back to the document,
         the same as the prompt card's close button (assets/dn-prompt.js). */
      if (state === 'gone') {
        if (el.parentNode) el.parentNode.removeChild(el);
        return;
      }
      el.setAttribute('data-state', state);
      if (state === 'signin') {
        offerSignIn();
        return;
      }
      say(COPY[state]);
    }

    function ask() {
      if (busy || !url || !variantId) return;
      busy = true;
      button.setAttribute('aria-busy', 'true');
      var status = 0;
      fetch(url, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        credentials: 'same-origin',
        body: JSON.stringify(payload(variantId, productId))
      })
        .then(function (r) {
          status = r.status;
          /* The store answers an unconfigured proxy with its own HTML 404 page. Reading that as JSON
             throws, and a throw here would reach the console; read it only when it says it is JSON. */
          if ((r.headers.get('content-type') || '').indexOf('application/json') === -1) return null;
          return r.json().catch(function () { return null; });
        })
        .catch(function () { status = 0; return null; })
        .then(function (body) {
          busy = false;
          button.removeAttribute('aria-busy');
          settle(outcome(status, body));
        });
    }

    button.addEventListener('click', ask);
  }

  return { outcome: outcome, payload: payload, boot: boot, COPY: COPY };
});
